import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';

import history from '@utils/history';
import HeaderedPageLayout from '@common/components/HeaderedPageLayout';
import { goNextJourneyStep } from '@journeys/sagas';
import { displayErrorMessage } from '@utils/displayErrorMessage';
import { createNotification, getNotifications } from '@notifications/sagas';
import styles from '../styles.module.scss';
import dialogStyles from '@common/components/ConfirmDeletionModal/styles.module.scss';
import ConfirmNotificationModal from '@common/components/ConfirmNotificationModal';
import AnimatedSuccessTick from '@common/components/AnimatedSuccessTick';
import {isPartner, USER_ROLE} from '@utils/authorisation';


export const Scheduled = ({ inDialog = false, onComplete }) => {
  const dispatch = useDispatch();
  const dimensions = useSelector(state => state.dimensions);
  const { user, selectedUser } = useSelector(state => state.user);

  const [eventType, setEventType] = useState(undefined);
  const [startTime, setStartTime] = useState(undefined);
  const [readyToProceed, setReadyToProceed] = useState(false);
  const [destination, setDestination] = useState('/dashboard');
  const [selectedId, setSelectedId] = useState(undefined);
  const [confirmNotificationOpen, setConfirmNotificationOpen] = useState(false);
  const [confirmNotificationParameters, setConfirmNotificationParameters] = useState({});

  useEffect(() => {
    const journeyId = localStorage.getItem('CALENDAR_REDIRECT_JOURNEY');
    const userId = localStorage.getItem('CALENDAR_REDIRECT_USER');
    const userName = localStorage.getItem('CALENDAR_REDIRECT_SPECIALIST_NAME');
    const destinationFromCookie = localStorage.getItem('CALENDAR_REDIRECT_DESTINATION');
    const next = localStorage.getItem('CALENDAR_REDIRECT_NEXT');//'Specialist' or 'Conveyancer'
    setSelectedId(localStorage.getItem('CALENDAR_REDIRECT_SELECTED_USER'));
    const timeString = localStorage.getItem('CALENDAR_REDIRECT_TIME');
    const eType = localStorage.getItem('CALENDAR_REDIRECT_EVENT_TYPE');
    const sTime = format(new Date(timeString), 'h:mm aa ccc dd MMMM');
    setEventType(eType);
    setStartTime(sTime);
    setDestination(destinationFromCookie);

    if ((userId || user?.id || selectedUser.id) && userName && (journeyId || userId || destination || next)) {

      dispatch(createNotification({
        body: {
          journey: journeyId ?? undefined,
          user: userId ?? selectedUser.id ?? user.id,
          type: 'Appointment',
          summary: `You have a ${eType} with ${userName} scheduled at`,
          summaryElements: [ sTime ],
          expirationDate: timeString ? new Date(timeString) : undefined
        },
        onError: (data) => {
          displayErrorMessage(data, 'Unable to create notification');
          if (!next) proceed();
        },
        onSuccess: () => {
          if (!next) proceed();
        }
      }));

      if (next) {
        const proceedNext = (notify) => {
          dispatch(goNextJourneyStep({
            id: journeyId,
            category: next,
            notify: notify,
            onError: (data) => {
              displayErrorMessage(data, 'Unable to switch to the next journey step');
              proceed();
            },
            onSuccess: () => {
              proceed();
            }
          }));
        };

        setConfirmNotificationParameters({
          yesAction: () => proceedNext(),
          noAction: () => proceedNext('no')
        });

        if (user?.roles?.length && !!user.roles.find((role) => role !== USER_ROLE.Customer) && !isPartner(user)) setConfirmNotificationOpen(true);
        else proceedNext();
      }

    } else {
      removeCookies();
      setReadyToProceed(true);
      history.push(`/dashboard`);
    }
  }, []); //eslint-disable-line

  const removeCookies = () => {
    localStorage.removeItem('CALENDAR_REDIRECT_JOURNEY');
    localStorage.removeItem('CALENDAR_REDIRECT_DESTINATION');
    localStorage.removeItem('CALENDAR_REDIRECT_NEXT');
    localStorage.removeItem('CALENDAR_REDIRECT_USER');
    localStorage.removeItem('CALENDAR_REDIRECT_SELECTED_USER');
    localStorage.removeItem('CALENDAR_REDIRECT_SPECIALIST_NAME');
    localStorage.removeItem('CALENDAR_REDIRECT_TIME');
    localStorage.removeItem('CALENDAR_REDIRECT_EVENT_TYPE');
  };

  const proceed = () => {
    removeCookies();
    setTimeout(() => {
      setReadyToProceed(true);
    }, 500);
  };

  const title = readyToProceed ? `Looking forward to our ${eventType}` : 'Processing...';
  const isAlreadyOnDestination = inDialog && (destination ?? '/dashboard') === history.location.pathname;
  const continueAction = () => {
    onComplete?.();
    if (isAlreadyOnDestination) {
      dispatch(getNotifications({
        userId: selectedUser?.id ?? user?.id,
        onSuccess: () => {},
        onError: (data) => displayErrorMessage(data, 'Unable to retrieve notifications')
      }));
    } else {
      history.push((destination ?? '/dashboard') + (selectedId ? `?selected_user=${selectedId}` : ''));
    }
  };
  const content = readyToProceed ? (
      <Grid container justifyContent="center">
        <Grid item className={styles.contentContainer}>
          <Grid
            container
            direction="column"
            spacing={2}
            justifyContent="space-between"
            alignItems="flex-end"
            alignContent="center"
          >
            <Grid item className={styles.successTickWrapper}>
              <AnimatedSuccessTick variant="orange" className={styles.successTick} />
            </Grid>
            <Grid item xs={12} className={`${styles.info} ${dimensions.isScreenLessMD ? styles.mobile : ''}`}>
              Our {eventType === 'call' ? 'call' : 'meeting'} is booked for {startTime}
            </Grid>
            <Grid item xs={12} className={styles.text}>
              You will receive a calendar invitation with all the details at your nominated email address.
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    ) : (
      <CircularProgress size={80} className={styles.cardProgress} />
    );

  if (inDialog) return <>
    <DialogTitle><Box className={`${dialogStyles.modalTitle} ${dimensions.isScreenLessMD ? dialogStyles.mobile : ''}`}>{title}</Box></DialogTitle>
    <DialogContent>{content}</DialogContent>
    <DialogActions>
      <Grid container justifyContent="flex-end">
        <Button className={dialogStyles.actionButton} variant="contained" disabled={!readyToProceed} onClick={continueAction}>{isAlreadyOnDestination ? 'CLOSE' : 'TO DASHBOARD'}</Button>
      </Grid>
    </DialogActions>
    <ConfirmNotificationModal open={confirmNotificationOpen} setOpen={setConfirmNotificationOpen} parameters={confirmNotificationParameters}/>
  </>;

  return <HeaderedPageLayout
    title={title}
    headerTitle={''}
    progress={null}
    displayAskSpecialistWidget={false}
    noXSPaddings
    backAction={() => history.push('/dashboard' + (selectedId ? `?selected_user=${selectedId}` : ''))}
    backCaption={'BACK'}
    backHidden={true}
    nextHidden={false}
    nextAction={continueAction}
    nextCaption={'CONTINUE'}
    nextDisabled={!readyToProceed}
    beigeContentBg={false}
  >
    {content}
    <ConfirmNotificationModal open={confirmNotificationOpen} setOpen={setConfirmNotificationOpen} parameters={confirmNotificationParameters}/>
  </HeaderedPageLayout>;
};

export default withRouter(Scheduled);
